import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import type { ModuleDefinition } from './moduleRegistry';
import type { AmlPepEntity, AmlPepStatus, LegalCase, LegalGroup, ReportData } from '../tokens/types';
import { PdfSection, PdfDataTable, PdfKpiGrid, PdfDivider } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenDataTable, ScreenKpiGrid, ScreenDivider } from '../primitives/ScreenPrimitives';
import { colors, coverFonts, fontFamily, overviewColors, ptToPx, space, typeScale } from '../tokens/designTokens';
import { fmtDate } from './formatUtils';

const flatten = (groups: LegalGroup[]): LegalCase[] => groups.flatMap((g) => g.cases);

const truncateParty = (s: string, max = 90): string => {
    if (!s) return '—';
    const cleaned = s.trim();
    if (cleaned.length <= max) return cleaned;
    return cleaned.slice(0, max).trimEnd() + '…';
};

// Per PM feedback: render full case strings, no truncation. PdfDataTable
// cells use `wrap` and rows use `wrap={false}`, so cells reflow to fit and
// the row grows to the tallest cell. The `truncateParty` helper still
// exists in this file for any local emergency reuse but is not called.
const toRows = (cases: LegalCase[]) =>
    cases.slice(0, 20).map((c) => ({
        caseNum: c.caseNum || '—',
        party: c.filedBy ? `${c.filedBy} → ${c.filedAgainst}` : c.filedAgainst || '—',
        category: c.category || '—',
        court: c.court || '—',
        status: c.status,
        filed: fmtDate(c.filingDate),
    }));

// Column widths sum to 100% across body width (~526pt at the new tightened
// page padding). Cells wrap if their content is long; row height grows.
const columns = [
    { key: 'caseNum', header: 'Case #', width: '18%' },
    { key: 'party', header: 'Parties', width: '30%' },
    { key: 'category', header: 'Category', width: '16%' },
    { key: 'court', header: 'Court', width: '14%' },
    { key: 'status', header: 'Status', width: '11%' },
    { key: 'filed', header: 'Filed', width: '11%' },
];

// ──────────────────────────────────────────────────────────────────────
// Regulatory Checks matrix (Figma 32:102215)
// ──────────────────────────────────────────────────────────────────────

const PF = coverFonts.pdf;
const SF = coverFonts.screen;

const matrixDesign = {
    gridGap: 11.34,
    // Trimmed cardPad 12.34 → 10 to give the title row more horizontal slack;
    // long entity names ("INCUSPAZE SOLUTIONS PRIVATE LIMITED") were clipping
    // the card right edge at the previous padding.
    cardPad: 10,
    cardRadius: 4,
    cardBorder: 0.5,
    cardBorderColor: overviewColors.border,
    cardBg: '#FFFFFF',

    // Title typography. The contract:
    //  - source text bound at the normalizer (28 chars max, see normalizers.ts)
    //  - rendered at 7.5pt uppercase with `flex: 1` so react-pdf measures
    //    against the card width and forces wrap when the line wouldn't fit
    //  - title row reserves a fixed 2-line minHeight so cards in a row stay
    //    equal-height regardless of whether names wrap (skill rule 3)
    //  - max characters × fontSize at this scale is guaranteed ≤ 2 lines in
    //    the ~237pt card content area, so overflow is structurally impossible
    titleSize: 7.5,
    titleTrackingEm: 0,
    titleLineHeight: 1.3,
    titleMaxLines: 2,
    titleColor: overviewColors.ink,
    titleGap: 8,

    // Settled on 5pt after balancing legibility vs page-fit. 6.5pt looked
    // airier but pushed a 9-dimension card past 270pt and caused page-bottom
    // overflow on the row that landed near a page break. 5pt keeps each row
    // visibly distinct and the worst-case card under ~250pt.
    rowDividerColor: '#F0EFE9',
    rowPadY: 5,
    rowLabelSize: 9,
    rowLabelColor: overviewColors.inkSoft,
    rowLabelLineHeight: 1.3,
    // Explicit minHeight on each dimension row so spacing is deterministic
    // regardless of layout context. Without this, paired cards that get
    // visually broken across a page boundary render with subtly different
    // row spacing — same content, two different vertical rhythms.
    // 5pt pad-top + 9pt × 1.3 ≈ 11.7pt label + 5pt pad-bottom ≈ 22pt.
    rowMinHeight: 22,
    dotSize: 7,

    statusBg: {
        clear: '#059669',
        watch: '#D97706',
        hit: '#DC2626',
        unavailable: '#9CA3AF',
    } as Record<AmlPepStatus, string>,

    // Tinted pill variants for the per-dimension tags. Solid statusBg is too
    // saturated for a small pill behind text — drops legibility. The tinted
    // bg + darker ink pair reads as a quiet status chip without shouting.
    statusPillBg: {
        clear: '#D1FAE5',
        watch: '#FEF3C7',
        hit: '#FEE2E2',
        unavailable: '#F3F4F6',
    } as Record<AmlPepStatus, string>,
    statusPillInk: {
        clear: '#047857',
        watch: '#B45309',
        hit: '#991B1B',
        unavailable: '#6B7280',
    } as Record<AmlPepStatus, string>,

    // Canonical color-meaning labels, sourced from the existing app-wide
    // RiskIndicatorConstants.ts so the PDF report uses the same vocabulary
    // as the dashboard's Regulatory Checks table. Order: best → worst → no-data.
    legend: [
        { status: 'clear' as AmlPepStatus, label: 'No Risk' },
        { status: 'watch' as AmlPepStatus, label: 'Potential Risk' },
        { status: 'hit' as AmlPepStatus, label: 'Confirmed Risk' },
        { status: 'unavailable' as AmlPepStatus, label: 'Not Available' },
    ],
} as const;

// Per-dimension reader-friendly descriptions. The Polus AML/PEP feed only
// returns numeric scores per check (PepCheck: 0, ApcCheck: 85, …) with no
// human description, so the matrix dots are otherwise unexplained in the
// printed report (no hover tooltips). These descriptions are rendered once
// in a legend strip below the color-status row so a first-time reader can
// decode what each dimension actually screens for. Wording mirrors the
// dashboard's Regulatory Checks panel so vocabulary is consistent across
// the product surface.
const DIMENSION_DESCRIPTIONS: Record<string, string> = {
    'Political Exposed Person':
        'Director or officer flagged on Politically Exposed Person (PEP) databases — heightened due-diligence warranted.',
    'Global Sanctions': 'Match against OFAC, UN and EU global sanctions lists.',
    'AML Penalties': 'Prior anti-money-laundering penalties or enforcement actions on record.',
    'Adverse Press Coverage': 'Negative media mentions linking the entity to fraud, litigation or misconduct.',
    'Indian Watchlists': 'Hits on RBI, SEBI, MCA, ED and other Indian regulatory watchlists.',
    'Global Watchlists': 'Hits on international law enforcement and regulator watchlists.',
    'State Owned Enterprises': 'Direct or indirect linkage to state-owned enterprises.',
    'Country Regimes Watchlist': 'Exposure to high-risk country regimes (FATF / sanctioned states).',
    "Other Countries' Watchlists": 'Hits on watchlists from jurisdictions outside India and the primary global lists.',
};

// Pair entities into 2-column rows (skill rule 2 — explicit rows, never flexWrap).
const pairsOf = <T,>(arr: T[]): Array<[T | undefined, T | undefined]> => {
    const out: Array<[T | undefined, T | undefined]> = [];
    for (let i = 0; i < arr.length; i += 2) out.push([arr[i], arr[i + 1]]);
    return out;
};

// Derive an entity's role from cross-referencing its name with the assessee
// org name and the list of director names. Surfaces "Subject Company",
// "Director", or "Related Entity" as a kicker under the card title so the
// reader can tell at a glance whose risk profile they're looking at.
const deriveEntityRole = (entity: AmlPepEntity, assesseeName: string, directorNames: string[]): string => {
    const n = entity.name.trim().toLowerCase();
    if (assesseeName && n === assesseeName.trim().toLowerCase()) return 'Subject Company';
    if (directorNames.some((d) => d.trim().toLowerCase() === n)) return 'Director';
    return entity.type === 'individual' ? 'Related Individual' : 'Related Entity';
};

// Compute the worst dimension status across an entity's regulatory checks.
// Drives the risk badge in the card header (RISK / POTENTIAL / CLEAR).
const worstStatus = (entity: AmlPepEntity): AmlPepStatus => {
    const statuses = entity.dimensions.map((d) => d.status);
    if (statuses.includes('hit')) return 'hit';
    if (statuses.includes('watch')) return 'watch';
    if (statuses.every((s) => s === 'unavailable')) return 'unavailable';
    return 'clear';
};

const RISK_BADGE_LABEL: Record<AmlPepStatus, string> = {
    clear: 'CLEAR',
    watch: 'POTENTIAL',
    hit: 'RISK',
    unavailable: 'N/A',
};

// Per-row tag labels — slightly different vocabulary from the card-level
// RISK_BADGE_LABEL so reader doesn't confuse the worst-status summary with
// each individual check's outcome. Numeric score is appended for non-clear,
// non-unavailable states so a "HIT" with 100 reads differently from a "HIT"
// with the minimum 80 threshold — the score itself is signal.
const DIMENSION_TAG_LABEL: Record<AmlPepStatus, string> = {
    clear: 'CLEAR',
    watch: 'REVIEW',
    hit: 'HIT',
    unavailable: 'N/A',
};
const formatDimensionTag = (status: AmlPepStatus, score?: number): string => {
    const base = DIMENSION_TAG_LABEL[status];
    if (status === 'clear' || status === 'unavailable') return base;
    if (score == null || !Number.isFinite(score)) return base;
    return `${base} · ${Math.round(score)}`;
};

const PdfRegulatoryCard: React.FC<{ entity: AmlPepEntity; role: string }> = ({ entity, role }) => {
    const status = worstStatus(entity);
    const badgeBg = matrixDesign.statusBg[status];
    return (
        <View
            wrap={false}
            style={{
                borderWidth: matrixDesign.cardBorder,
                borderColor: matrixDesign.cardBorderColor,
                borderRadius: matrixDesign.cardRadius,
                backgroundColor: matrixDesign.cardBg,
                padding: matrixDesign.cardPad,
            }}
        >
            {/* Role kicker — own thin row above the name so the tag has room
                to right-align against the entity title. */}
            <Text
                style={{
                    fontSize: matrixDesign.titleSize - 1,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: matrixDesign.titleColor,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                    marginBottom: 4,
                }}
            >
                {role}
            </Text>
            {/* Title row: entity name (flex:1, wraps to 2 lines max) + risk
                badge pinned to the right. `alignItems: 'flex-start'` lets the
                badge sit at the top of the row even when the title wraps to
                a second line, which is the visual the user requested. The
                2-line minHeight on the wrapper keeps cards in the same row
                equal-height regardless of whether names wrap. */}
            <View
                style={{
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 6,
                    minHeight: matrixDesign.titleSize * matrixDesign.titleLineHeight * matrixDesign.titleMaxLines,
                    marginBottom: matrixDesign.titleGap,
                }}
            >
                <Text
                    style={{
                        flex: 1,
                        fontSize: matrixDesign.titleSize,
                        fontFamily: PF.sans,
                        fontWeight: 600,
                        color: matrixDesign.titleColor,
                        letterSpacing: matrixDesign.titleSize * matrixDesign.titleTrackingEm,
                        lineHeight: matrixDesign.titleLineHeight,
                        textTransform: 'uppercase',
                    }}
                >
                    {entity.name}
                </Text>
                <View
                    style={{
                        backgroundColor: badgeBg,
                        paddingHorizontal: 5,
                        paddingTop: 1,
                        paddingBottom: 2,
                        borderRadius: 2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Text
                        style={{ fontSize: 6, color: '#FFFFFF', fontFamily: PF.sans, fontWeight: 600, letterSpacing: 0.4, lineHeight: 1 }}
                    >
                        {RISK_BADGE_LABEL[status]}
                    </Text>
                </View>
            </View>
            {entity.dimensions.map((dim, i) => {
                const isLast = i === entity.dimensions.length - 1;
                const tagText = formatDimensionTag(dim.status, dim.score);
                const tagBg = matrixDesign.statusPillBg[dim.status];
                const tagInk = matrixDesign.statusPillInk[dim.status];
                return (
                    <View
                        key={dim.label}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 6,
                            minHeight: matrixDesign.rowMinHeight,
                            paddingTop: matrixDesign.rowPadY,
                            paddingBottom: matrixDesign.rowPadY,
                            borderBottomWidth: isLast ? 0 : matrixDesign.cardBorder,
                            borderBottomColor: matrixDesign.rowDividerColor,
                        }}
                    >
                        {/* Label left, flex:1 so it wraps cleanly (skill rule 4). */}
                        <Text
                            style={{
                                flex: 1,
                                fontSize: matrixDesign.rowLabelSize,
                                fontFamily: PF.sans,
                                color: matrixDesign.rowLabelColor,
                                lineHeight: matrixDesign.rowLabelLineHeight,
                            }}
                        >
                            {dim.label}
                        </Text>
                        {/* Color-coded status pill, right-aligned. Three-part
                            centering (skill rule 9): lineHeight:1 on Text +
                            center align/justify on View + asymmetric padding. */}
                        <View
                            style={{
                                backgroundColor: tagBg,
                                paddingHorizontal: 5,
                                paddingTop: 1.5,
                                paddingBottom: 2.5,
                                borderRadius: 2,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: 6.5,
                                    color: tagInk,
                                    fontFamily: PF.sans,
                                    fontWeight: 600,
                                    letterSpacing: 0.4,
                                    lineHeight: 1,
                                }}
                            >
                                {tagText}
                            </Text>
                        </View>
                    </View>
                );
            })}
        </View>
    );
};

const PdfRegulatoryMatrix: React.FC<{ entities: AmlPepEntity[]; assesseeName: string; directorNames: string[] }> = ({
    entities,
    assesseeName,
    directorNames,
}) => {
    return (
        <View style={{ flexDirection: 'column', gap: matrixDesign.gridGap }}>
            {pairsOf(entities).map((row, ri) => (
                <View key={ri} style={{ flexDirection: 'row', gap: matrixDesign.gridGap }} wrap={false}>
                    {row.map((entity, ci) =>
                        entity ? (
                            <View key={ci} style={{ flex: 1 }}>
                                <PdfRegulatoryCard entity={entity} role={deriveEntityRole(entity, assesseeName, directorNames)} />
                            </View>
                        ) : (
                            <View key={ci} style={{ flex: 1 }} />
                        ),
                    )}
                </View>
            ))}
        </View>
    );
};

const ScreenRegulatoryCard: React.FC<{ entity: AmlPepEntity; role: string }> = ({ entity, role }) => {
    const status = worstStatus(entity);
    const badgeBg = matrixDesign.statusBg[status];
    return (
        <div
            style={{
                border: `${ptToPx(matrixDesign.cardBorder)}px solid ${matrixDesign.cardBorderColor}`,
                borderRadius: ptToPx(matrixDesign.cardRadius),
                background: matrixDesign.cardBg,
                padding: ptToPx(matrixDesign.cardPad),
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(matrixDesign.titleSize - 1),
                    fontFamily: SF.sans,
                    fontWeight: 600,
                    color: matrixDesign.titleColor,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    marginBottom: 6,
                }}
            >
                {role}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                    minHeight: ptToPx(matrixDesign.titleSize * matrixDesign.titleLineHeight * matrixDesign.titleMaxLines),
                    marginBottom: ptToPx(matrixDesign.titleGap),
                }}
            >
                <span
                    style={{
                        flex: 1,
                        fontSize: ptToPx(matrixDesign.titleSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        color: matrixDesign.titleColor,
                        letterSpacing: `${matrixDesign.titleTrackingEm}em`,
                        lineHeight: matrixDesign.titleLineHeight,
                        textTransform: 'uppercase',
                        wordBreak: 'break-word',
                    }}
                >
                    {entity.name}
                </span>
                <span
                    style={{
                        background: badgeBg,
                        color: '#FFFFFF',
                        fontSize: 9,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        padding: '1px 5px',
                        borderRadius: 2,
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                    }}
                >
                    {RISK_BADGE_LABEL[status]}
                </span>
            </div>
            {entity.dimensions.map((dim, i) => {
                const isLast = i === entity.dimensions.length - 1;
                return (
                    <div
                        key={dim.label}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: ptToPx(6),
                            minHeight: ptToPx(matrixDesign.rowMinHeight),
                            paddingTop: ptToPx(matrixDesign.rowPadY),
                            paddingBottom: ptToPx(matrixDesign.rowPadY),
                            borderBottom: isLast ? 'none' : `${ptToPx(matrixDesign.cardBorder)}px solid ${matrixDesign.rowDividerColor}`,
                        }}
                    >
                        <span
                            style={{
                                flex: 1,
                                fontSize: ptToPx(matrixDesign.rowLabelSize),
                                fontFamily: SF.sans,
                                color: matrixDesign.rowLabelColor,
                                lineHeight: matrixDesign.rowLabelLineHeight,
                            }}
                        >
                            {dim.label}
                        </span>
                        <span
                            style={{
                                background: matrixDesign.statusPillBg[dim.status],
                                color: matrixDesign.statusPillInk[dim.status],
                                fontSize: 10,
                                fontFamily: SF.sans,
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                padding: '2px 6px',
                                borderRadius: 3,
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {formatDimensionTag(dim.status, dim.score)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// AML_PEP_DIMENSIONS labels (mirrored from normalizers.ts) so the legend
// can list every check the matrix renders, in the same order. Re-declared
// locally to keep LegalRisk.tsx free of a normalizer import — the labels
// are stable and live in two places.
const DIMENSION_LABELS_ORDERED: string[] = [
    'Political Exposed Person',
    'Global Sanctions',
    'AML Penalties',
    'Adverse Press Coverage',
    'Indian Watchlists',
    'Global Watchlists',
    'State Owned Enterprises',
    'Country Regimes Watchlist',
    "Other Countries' Watchlists",
];

// Color legend rendered right above the Regulatory Checks matrix. Explains
// what each dot color means AND what each AML/PEP check screens for, so a
// first-time reader can decode the matrix without flipping back. Labels
// mirror RiskIndicatorConstants.RISK_TYPES so the report uses the same
// vocabulary as the dashboard. The dimension descriptions are local since
// the Polus AML/PEP feed only returns numeric scores per check — there are
// no hover tooltips in the printed PDF, so the meaning has to be inline.
const PdfRegulatoryLegend: React.FC = () => (
    // Legend total height ≈ 130pt. wrap=false forces the whole legend to the
    // next page if it doesn't fit on the current one — splitting it across
    // pages would orphan the color-status row from the description grid.
    <View style={{ paddingTop: 2, paddingBottom: 8 }} wrap={false}>
        {/* Color → status row */}
        <View
            style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: 8,
            }}
            wrap={false}
        >
            {matrixDesign.legend.map(({ status, label }) => (
                <View key={status} style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    {/* Tinted pill sample matching the per-row tags below — keeps
                        the legend visually consistent with what the reader sees
                        inside each card. */}
                    <View
                        style={{
                            backgroundColor: matrixDesign.statusPillBg[status],
                            paddingHorizontal: 5,
                            paddingTop: 1.5,
                            paddingBottom: 2.5,
                            borderRadius: 2,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Text
                            style={{
                                fontSize: 6.5,
                                color: matrixDesign.statusPillInk[status],
                                fontFamily: PF.sans,
                                fontWeight: 600,
                                letterSpacing: 0.4,
                                lineHeight: 1,
                            }}
                        >
                            {DIMENSION_TAG_LABEL[status]}
                        </Text>
                    </View>
                    <Text style={{ fontSize: typeScale.micro, fontFamily: PF.sans, color: colors.muted, lineHeight: 1.3 }}>{label}</Text>
                </View>
            ))}
        </View>
        {/* Dimension descriptions — 2-column grid built as explicit row pairs
            (skill rule 2: never flexWrap a grid). Each item: bold label on
            the first line, then a short description below. */}
        <View
            style={{
                padding: 8,
                borderRadius: 4,
                borderWidth: 0.5,
                borderColor: overviewColors.border,
                backgroundColor: '#FAFAF8',
            }}
        >
            <Text
                style={{
                    fontSize: 6.5,
                    fontFamily: PF.sans,
                    fontWeight: 600,
                    color: colors.ink40,
                    letterSpacing: 0.6,
                    textTransform: 'uppercase',
                    marginBottom: 6,
                    lineHeight: 1,
                }}
            >
                What each check screens for
            </Text>
            {pairsOf(DIMENSION_LABELS_ORDERED).map((row, ri) => (
                <View key={ri} style={{ flexDirection: 'row', gap: 10, marginTop: ri === 0 ? 0 : 6 }} wrap={false}>
                    {row.map((label, ci) =>
                        label ? (
                            <View key={ci} style={{ flex: 1 }}>
                                <Text
                                    style={{
                                        fontSize: 7,
                                        fontFamily: PF.sans,
                                        fontWeight: 600,
                                        color: colors.ink20,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {label}
                                </Text>
                                <Text
                                    style={{
                                        fontSize: 6.5,
                                        fontFamily: PF.sans,
                                        color: colors.muted,
                                        lineHeight: 1.4,
                                        marginTop: 1,
                                    }}
                                >
                                    {DIMENSION_DESCRIPTIONS[label] ?? ''}
                                </Text>
                            </View>
                        ) : (
                            <View key={ci} style={{ flex: 1 }} />
                        ),
                    )}
                </View>
            ))}
        </View>
    </View>
);

const ScreenRegulatoryLegend: React.FC = () => (
    <div style={{ paddingTop: 4, paddingBottom: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 10 }}>
            {matrixDesign.legend.map(({ status, label }) => (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                        style={{
                            background: matrixDesign.statusPillBg[status],
                            color: matrixDesign.statusPillInk[status],
                            fontSize: 10,
                            fontFamily: SF.sans,
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            padding: '2px 6px',
                            borderRadius: 3,
                        }}
                    >
                        {DIMENSION_TAG_LABEL[status]}
                    </span>
                    <span style={{ fontSize: 11, color: colors.muted, fontFamily: SF.sans }}>{label}</span>
                </div>
            ))}
        </div>
        <div
            style={{
                padding: 12,
                borderRadius: 6,
                border: `1px solid ${overviewColors.border}`,
                background: '#FAFAF8',
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: colors.ink40,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                }}
            >
                What each check screens for
            </div>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    columnGap: 16,
                    rowGap: 10,
                }}
            >
                {DIMENSION_LABELS_ORDERED.map((label) => (
                    <div key={label}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: colors.ink20, lineHeight: 1.3 }}>{label}</div>
                        <div style={{ fontSize: 10.5, color: colors.muted, lineHeight: 1.45, marginTop: 2 }}>
                            {DIMENSION_DESCRIPTIONS[label] ?? ''}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const ScreenRegulatoryMatrix: React.FC<{ entities: AmlPepEntity[]; assesseeName: string; directorNames: string[] }> = ({
    entities,
    assesseeName,
    directorNames,
}) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: ptToPx(matrixDesign.gridGap) }}>
            {pairsOf(entities).map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: ptToPx(matrixDesign.gridGap) }}>
                    {row.map((entity, ci) =>
                        entity ? (
                            <div key={ci} style={{ flex: 1, minWidth: 0 }}>
                                <ScreenRegulatoryCard entity={entity} role={deriveEntityRole(entity, assesseeName, directorNames)} />
                            </div>
                        ) : (
                            <div key={ci} style={{ flex: 1 }} />
                        ),
                    )}
                </div>
            ))}
        </div>
    );
};

// Sub-section heading reused for "Regulatory Checks" / "Legal Cases".
const PdfH3: React.FC<{ children: React.ReactNode; subtitle?: string; topGap?: number }> = ({ children, subtitle, topGap = space.md }) => (
    <View style={{ marginTop: topGap }}>
        <Text style={{ fontSize: typeScale.body, fontFamily: fontFamily.sansBold, color: colors.ink }}>{children}</Text>
        {subtitle ? <Text style={{ fontSize: typeScale.micro, color: colors.muted, marginTop: 2 }}>{subtitle}</Text> : null}
    </View>
);

const ScreenH3: React.FC<{ children: React.ReactNode; subtitle?: string; topGap?: number }> = ({ children, subtitle, topGap = 16 }) => (
    <div style={{ marginTop: topGap }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.ink }}>{children}</div>
        {subtitle ? <div style={{ fontSize: 11, color: colors.muted, marginTop: 2 }}>{subtitle}</div> : null}
    </div>
);

const PdfLegalRisk: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const lr = data.legalRisk;
    if (!lr) return null;
    const against = flatten(lr.casesAgainst);
    const by = flatten(lr.casesBy);
    const assesseeName = data.identity?.orgName ?? '';
    const directorNames = (data.management?.profiles ?? []).map((p) => p.name).filter(Boolean) as string[];
    return (
        <PdfSection kicker="Risk" title="Regulatory & Legal" index={index} breakBefore={breakBefore}>
            <PdfKpiGrid
                items={[
                    { label: 'Cases against', value: String(against.length) },
                    { label: 'Cases filed by', value: String(by.length) },
                    { label: 'AML / PEP hits', value: String(lr.amlPepCount) },
                    { label: 'Last refreshed', value: fmtDate(lr.lastFetchedDate) },
                ]}
            />

            {lr.amlPepEntities && lr.amlPepEntities.length > 0 ? (
                <View style={{ marginTop: space.md }} minPresenceAhead={160}>
                    <PdfH3 subtitle="AML, PEP and watchlist screening per entity" topGap={0}>
                        Regulatory Checks
                    </PdfH3>
                    <View style={{ marginTop: 8 }}>
                        <PdfRegulatoryLegend />
                        <PdfRegulatoryMatrix entities={lr.amlPepEntities} assesseeName={assesseeName} directorNames={directorNames} />
                    </View>
                </View>
            ) : null}

            {against.length || by.length ? <PdfDivider /> : null}

            {against.length ? (
                <View minPresenceAhead={90}>
                    {/* minPresenceAhead keeps the heading on the same page as
                        at least one row of the table that follows. Pure
                        additive — the heading + first row pair stays together
                        and the rest of the table flows naturally. */}
                    <PdfH3 subtitle="Cases filed against the company or its directors" topGap={0}>
                        Cases against
                    </PdfH3>
                    <View style={{ marginTop: 6 }}>
                        <PdfDataTable columns={columns} rows={toRows(against)} />
                    </View>
                    {against.length > 20 ? (
                        <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft, marginTop: 4 }}>
                            +{against.length - 20} additional cases omitted.
                        </Text>
                    ) : null}
                </View>
            ) : null}
            {by.length ? (
                <View minPresenceAhead={90}>
                    <PdfH3 subtitle="Cases filed by the company or its directors">Cases filed by</PdfH3>
                    <View style={{ marginTop: 6 }}>
                        <PdfDataTable columns={columns} rows={toRows(by)} />
                    </View>
                </View>
            ) : null}
        </PdfSection>
    );
};

const ScreenLegalRisk: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const lr = data.legalRisk;
    if (!lr) return null;
    const against = flatten(lr.casesAgainst);
    const by = flatten(lr.casesBy);
    const assesseeName = data.identity?.orgName ?? '';
    const directorNames = (data.management?.profiles ?? []).map((p) => p.name).filter(Boolean) as string[];
    return (
        <ScreenSection kicker="Risk" title="Regulatory & Legal" index={index}>
            <ScreenKpiGrid
                items={[
                    { label: 'Cases against', value: String(against.length) },
                    { label: 'Cases filed by', value: String(by.length) },
                    { label: 'AML / PEP hits', value: String(lr.amlPepCount) },
                    { label: 'Last refreshed', value: fmtDate(lr.lastFetchedDate) },
                ]}
            />
            {lr.amlPepEntities && lr.amlPepEntities.length > 0 ? (
                <div style={{ marginTop: 16 }}>
                    <ScreenH3 subtitle="AML, PEP and watchlist screening per entity" topGap={0}>
                        Regulatory Checks
                    </ScreenH3>
                    <div style={{ marginTop: 12 }}>
                        <ScreenRegulatoryLegend />
                        <ScreenRegulatoryMatrix entities={lr.amlPepEntities} assesseeName={assesseeName} directorNames={directorNames} />
                    </div>
                </div>
            ) : null}
            {against.length || by.length ? <ScreenDivider /> : null}
            {against.length ? (
                <>
                    <ScreenH3 subtitle="Cases filed against the company or its directors" topGap={0}>
                        Cases against
                    </ScreenH3>
                    <div style={{ marginTop: 8 }}>
                        <ScreenDataTable columns={columns} rows={toRows(against)} />
                    </div>
                </>
            ) : null}
            {by.length ? (
                <>
                    <ScreenH3 subtitle="Cases filed by the company or its directors">Cases filed by</ScreenH3>
                    <div style={{ marginTop: 8 }}>
                        <ScreenDataTable columns={columns} rows={toRows(by)} />
                    </div>
                </>
            ) : null}
        </ScreenSection>
    );
};

export const legalRiskModule: ModuleDefinition = {
    meta: {
        id: 'legalRisk',
        title: 'Regulatory & Legal',
        description: 'Compliance checks, litigation exposure and AML/PEP screening per entity.',
        dataSource: 'Legal Cases + AML/PEP',
    },
    hasData: (d) => {
        const lr = d.legalRisk;
        if (!lr) return false;
        return (
            lr.casesAgainst.some((g) => g.cases.length > 0) ||
            lr.casesBy.some((g) => g.cases.length > 0) ||
            lr.amlPepCount > 0 ||
            (lr.amlPepEntities ?? []).length > 0
        );
    },
    Pdf: PdfLegalRisk,
    Screen: ScreenLegalRisk,
};
